import { cn } from "@/lib/utils";

const SPINNER_SIZES = {
  sm: "h-4 w-4 border-2",
  md: "h-6 w-6 border-2",
  lg: "h-9 w-9 border-[3px]",
} as const;

/**
 * The single canonical loading spinner: a ring that fades from a muted track to
 * the primary accent. Use everywhere a loading state is needed — standalone,
 * inside buttons (size="sm"), or composed into PageLoader/SectionLoader.
 */
export function Spinner({
  size = "md",
  className,
}: {
  size?: keyof typeof SPINNER_SIZES;
  className?: string;
}) {
  return (
    <span
      role="status"
      aria-label="Loading"
      className={cn(
        "inline-block animate-spin rounded-full border-muted-foreground/25 border-t-primary",
        SPINNER_SIZES[size],
        className,
      )}
    />
  );
}

/**
 * Centered full-area loader for whole-page / whole-route loading states. The
 * label pulses gently so the wait reads as active rather than stalled.
 */
export function PageLoader({
  label,
  className,
}: {
  label?: string;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "flex min-h-[50vh] flex-col items-center justify-center gap-4",
        className,
      )}
    >
      <Spinner size="lg" />
      {label ? (
        <p className="animate-pulse text-sm text-muted-foreground">{label}</p>
      ) : null}
    </div>
  );
}
